import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import LinkChildDialog from '../../components/parent/LinkChildDialog';
import { useAuth } from '../../contexts/AuthContext';
import { getChildren, getChildProgress } from '../../services/parentService';
import { getRankedRoster } from '../../services/rankedService';
import { Child, ChildProgress } from '../../types/parent';
import { RankedRosterEntry } from '../../types/ranked';
import RankedSummaryStrip from '../../components/ranked/RankedSummaryStrip';

interface ChildWithProgress {
  child: Child;
  progress: ChildProgress | null;
  loading: boolean;
}

function ParentDashboardPage() {
  const { authState } = useAuth();
  const { user } = authState;

  const [children, setChildren] = useState<ChildWithProgress[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
  const [roster, setRoster] = useState<Map<number, RankedRosterEntry>>(new Map());
  const [totalRanked, setTotalRanked] = useState<number>(0);
  const [rosterLoading, setRosterLoading] = useState<boolean>(true);

  /**
   * One loader for both the initial mount and the post-link refresh. These
   * were two verbatim copies of the same twenty lines; the ranked fetch would
   * have made it three, and a third copy is how they start to drift.
   */
  const loadChildren = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const childrenData = await getChildren();

      setChildren(
        childrenData.map(child => ({ child, progress: null, loading: true }))
      );

      const progressResults = await Promise.all(
        childrenData.map(async (child, index) => {
          try {
            return { index, progressData: await getChildProgress(child.id) };
          } catch (err) {
            console.error(`Error fetching progress for child ${child.id}:`, err);
            return { index, progressData: null };
          }
        })
      );

      setChildren(prev => {
        const updated = [...prev];
        progressResults.forEach(({ index, progressData }) => {
          updated[index] = { ...updated[index], progress: progressData, loading: false };
        });
        return updated;
      });

      // Ranked standing for every child in ONE request. Deliberately caught
      // separately from the progress fetch: a ranked failure must leave the
      // existing stats on screen rather than blanking a working card.
      setRosterLoading(true);
      try {
        const rankedRoster = await getRankedRoster(childrenData.map(child => child.id));
        setRoster(new Map(rankedRoster.entries.map(entry => [entry.userId, entry])));
        setTotalRanked(rankedRoster.totalRanked);
      } catch (err) {
        console.error('Error fetching ranked roster:', err);
        setRoster(new Map());
      } finally {
        setRosterLoading(false);
      }
    } catch (err) {
      console.error('Error fetching children:', err);
      setError(err instanceof Error ? err.message : 'Failed to load children data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false);
    loadChildren();
  };

  // Calculate aggregate stats
  const aggregateStats = children.reduce(
    (acc, { progress }) => {
      if (progress) {
        acc.totalSubmissions += progress.totalSubmissions;
        acc.totalCorrect += progress.correctSubmissions;
        acc.totalPoints += progress.totalPoints;
        acc.totalAchievements += progress.achievementsEarned;
      }
      return acc;
    },
    { totalSubmissions: 0, totalCorrect: 0, totalPoints: 0, totalAchievements: 0 }
  );

  const overallAccuracy = aggregateStats.totalSubmissions > 0
    ? Math.round((aggregateStats.totalCorrect / aggregateStats.totalSubmissions) * 100)
    : 0;

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          data-testid="welcome-message"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            letterSpacing: '-0.01em',
            fontSize: '1.5rem',
            lineHeight: 1.3
          }}
        >
          Welcome, {user?.firstName}!
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: '0.95rem',
            maxWidth: '90%',
            mb: 2
          }}
        >
          Monitor your children's progress and achievements.
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column - Children List */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} data-demo-id="parent-children-card">
            <CardHeader
              title="My Children"
              action={
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => setLinkDialogOpen(true)}
                  data-testid="link-child-button"
                >
                  Link Child
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : children.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    You don't have any children linked to your account yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Children are automatically linked when you register as a parent.
                    If you need to link an existing student, please contact support.
                  </Typography>
                </Box>
              ) : (
                children.map(({ child, progress, loading: progressLoading }) => (
                  <Paper
                    key={child.id}
                    elevation={1}
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: 2,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    {/* Child Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          mr: 2,
                          bgcolor: 'primary.main',
                          fontSize: '1.5rem'
                        }}
                      >
                        {child.firstName?.charAt(0)}{child.lastName?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {child.firstName} {child.lastName}
                        </Typography>
                        {progress && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            {progress.grade && (
                              <Chip
                                size="small"
                                label={`Grade ${progress.grade}`}
                                icon={<SchoolIcon />}
                              />
                            )}
                            {progress.school && (
                              <Chip
                                size="small"
                                label={progress.school}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        component={RouterLink}
                        to={`/parent/children/${child.id}`}
                        endIcon={<ArrowForwardIcon />}
                        data-testid={`view-details-${child.id}`}
                      >
                        View Details
                      </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Ranked standing. Above the problem-set stats because it
                        is the thing a parent asks about first, and rendered
                        even when absent so "not ranked yet" is stated rather
                        than left as a gap. */}
                    <Box sx={{ mb: 2 }} data-testid={`child-ranked-${child.id}`}>
                      <RankedSummaryStrip
                        entry={roster.get(child.id) ?? null}
                        totalRanked={totalRanked}
                        loading={rosterLoading}
                      />
                    </Box>

                    {/* Progress Stats */}
                    {progressLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : progress ? (
                      <Grid container spacing={2} data-testid={`child-progress-${child.id}`}>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight="bold" color="primary" data-testid={`submissions-${child.id}`}>
                              {progress.totalSubmissions}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Problems Solved
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight="bold" color="success.main" data-testid={`accuracy-${child.id}`}>
                              {progress.accuracyPercentage}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Accuracy
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight="bold" color="secondary.main" data-testid={`points-${child.id}`}>
                              {progress.totalPoints.toFixed(0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Points
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight="bold" color="warning.main" data-testid={`achievements-${child.id}`}>
                              {progress.achievementsEarned}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Achievements
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        No progress data available
                      </Typography>
                    )}

                    {/* Enrollments Info */}
                    {progress && progress.totalEnrollments > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            Active Enrollments: {progress.activeEnrollments} of {progress.totalEnrollments}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(progress.activeEnrollments / progress.totalEnrollments) * 100}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    )}
                  </Paper>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary Stats */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader title="Overall Statistics" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.primary.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{children.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Children</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.secondary.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{aggregateStats.totalSubmissions}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Problems</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.success.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{overallAccuracy}%</Typography>
                    <Typography variant="body2" color="text.secondary">Avg Accuracy</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.warning.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <TrophyIcon sx={{ color: 'warning.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{aggregateStats.totalAchievements}</Typography>
                    <Typography variant="body2" color="text.secondary">Achievements</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader title="Quick Actions" />
            <Divider />
            <CardContent>
              <Typography variant="body2" paragraph color="text.secondary">
                Enroll your children in sessions, monitor their progress, and track their achievements.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  component={RouterLink}
                  to="/parent/enroll"
                  startIcon={<SchoolIcon />}
                  sx={{ mb: 1 }}
                  data-testid="enroll-children-button"
                >
                  Enroll Children
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={{ mb: 1 }}
                >
                  View All Enrollments
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                >
                  View All Achievements
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Link Child Dialog */}
      <LinkChildDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSuccess={handleLinkSuccess}
      />
    </MainLayout>
  );
}

export default ParentDashboardPage;
